import { FC, ReactNode, Ref, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import isNil from 'lodash/isNil';

/**
 * Use this as a generic drop down solution
 *
 * Assign toggle / close / open functionality to elements by using data tags, e.g:
 * <GenericDropDown className="default-dd-nav">
 *     <a className="bt-dd" data-toggle-dropdown="true">Countries</a>
 *     <div className="default-dd" data-close-dropdown="true">
 *         <ul>
 *             <li onClick="this.selectCountry">Germany</li>
 *             <li onClick="this.selectCountry">USA</li>
 *             <li data-close-dropdown="false" onClick="this.doSomething">Do something else and don't close dropdown</li>
 *         </ul>
 *     </div>
 * </GenericDropDown>
 */

interface Props {
    className?: string;
    children: ReactNode;
    showDropdown?: boolean;
    onOpenListener?: () => void;
    onCloseListener?: () => void;
    preventCloseDropdown?: boolean;
    title?: string;
    dataDisplayname?: string;
    ref?: any;
}
const GenericDropDown: FC<Props> = ({
    onOpenListener = () => {},
    onCloseListener = () => {},
    showDropdown = null,
    preventCloseDropdown = false,
    className,
    children,
    title,
    dataDisplayname
}) => {
    const ref = useRef() as Ref<HTMLDivElement>;
    const [showDropdownState, setShowDropdownState] = useState(false);
    const closeDropdown = useCallback(() => {
        setShowDropdownState(false);

        onCloseListener();
    }, [onCloseListener]);

    useEffect(() => {
        const checkDropdownOutsideClick = event => {
            // skip clicks in own element, only close on outside click
            if ((ref && ref === event.target) || (ref as any)?.current?.contains(event.target)) {
                return;
            }
            closeDropdown();
        };
        if (showDropdownState) {
            window.addEventListener('touchstart', checkDropdownOutsideClick);
            window.addEventListener('click', checkDropdownOutsideClick);
        } else {
            window.removeEventListener('touchstart', checkDropdownOutsideClick);
            window.removeEventListener('click', checkDropdownOutsideClick);
        }
        return () => {
            window.removeEventListener('touchstart', checkDropdownOutsideClick);
            window.removeEventListener('click', checkDropdownOutsideClick);
        };
    }, [closeDropdown, showDropdownState]);

    useEffect(() => {
        if (!isNil(showDropdownState) && showDropdown !== showDropdownState) {
            setShowDropdownState(showDropdownState);
        }
    }, [showDropdown, showDropdownState]);

    function toggleDropdown() {
        showDropdownState ? closeDropdown() : openDropdown();
    }

    function openDropdown() {
        setShowDropdownState(true);

        onOpenListener();
    }

    function clickHandler(event) {
        // iterate from "most inner" html element to top parent element
        let node = event.target;
        while (true) {
            if (node === event.currentTarget) {
                break;
            }

            if (node.hasAttribute('data-close-dropdown')) {
                if (node.getAttribute('data-close-dropdown') === 'true') {
                    if (preventCloseDropdown) {
                        break;
                    }
                    closeDropdown();
                }
                break;
            }

            if (node.hasAttribute('data-toggle-dropdown')) {
                if (node.getAttribute('data-toggle-dropdown') === 'true') {
                    toggleDropdown();
                }
                break;
            }

            if (node.hasAttribute('data-open-dropdown')) {
                if (node.getAttribute('data-open-dropdown') === 'true') {
                    openDropdown();
                }
                break;
            }

            node = node.parentNode;
        }
    }

    const applyDefaultDropdownStyle = !className;
    const mainClasses = classNames(className, {
        'default-dd-nav': applyDefaultDropdownStyle,
        active: showDropdownState
    });

    return (
        <div
            data-testid="generic-dropdown"
            ref={ref}
            className={mainClasses}
            onClick={clickHandler}
            title={title}
            data-displayname={dataDisplayname}
        >
            {children}
        </div>
    );
};

export default GenericDropDown;
